"use client";
import * as React from "react";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";

import { cn } from "../../lib/utils";
import { Button } from "./button";
import { Calendar } from "./calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

function DatePicker({ value, onChange, placeholder = "Pick a date", id, className, ...props }) {
  const [open, setOpen] = React.useState(false);

  // Parse string dates (e.g. "2024-01-15") into Date objects
  const dateValue = React.useMemo(() => {
    if (!value) return undefined;
    if (value instanceof Date) return value;
    const parsed = new Date(value + "T00:00:00");
    return isNaN(parsed.getTime()) ? undefined : parsed;
  }, [value]);

  const handleSelect = (date) => {
    if (date) {
      // Output as YYYY-MM-DD string to stay compatible with formik
      const formatted = format(date, "yyyy-MM-dd");
      onChange(formatted);
    } else {
      onChange("");
    }
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal h-10",
            "bg-[#222227] border-[#212123] text-white hover:bg-[#2a2a30] hover:text-white",
            !dateValue && "text-[#9ca3af]",
            className
          )}
          type="button"
          {...props}
        >
          <CalendarIcon className="mr-2 h-4 w-4 text-[#9ca3af]" />
          {dateValue ? format(dateValue, "PPP") : <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0 bg-[#15151A] border-[#212123]"
        align="start"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <Calendar
          mode="single"
          selected={dateValue}
          onSelect={handleSelect}
          initialFocus
          className="bg-[#15151A] text-white rounded-md"
          classNames={{
            months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
            month: "space-y-4",
            caption: "flex justify-center pt-1 relative items-center text-white",
            caption_label: "text-sm font-medium text-white",
            nav: "space-x-1 flex items-center",
            nav_button:
              "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 text-white border border-[#212123] rounded-md inline-flex items-center justify-center hover:bg-[#222227]",
            nav_button_previous: "absolute left-1",
            nav_button_next: "absolute right-1",
            table: "w-full border-collapse space-y-1",
            head_row: "flex",
            head_cell: "text-[#9ca3af] rounded-md w-9 font-normal text-[0.8rem]",
            row: "flex w-full mt-2",
            cell: "h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-[#3b82f6]/50 [&:has([aria-selected])]:bg-[#3b82f6] first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
            day: "h-9 w-9 p-0 font-normal text-white hover:bg-[#222227] rounded-md inline-flex items-center justify-center aria-selected:opacity-100 cursor-pointer",
            day_range_end: "day-range-end",
            day_selected:
              "bg-[#3b82f6] text-white hover:bg-[#2d63bb] hover:text-white focus:bg-[#3b82f6] focus:text-white",
            day_today: "bg-[#222227] text-white",
            day_outside:
              "day-outside text-[#9ca3af] opacity-50 aria-selected:bg-[#3b82f6]/50 aria-selected:text-white aria-selected:opacity-30",
            day_disabled: "text-[#9ca3af] opacity-50",
            day_range_middle:
              "aria-selected:bg-[#222227] aria-selected:text-white",
            day_hidden: "invisible",
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

export { DatePicker };
